import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, deleteToken, Messaging } from 'firebase/messaging';
import { firebaseConfig } from '../firebase-config';

const API_URL = '/cargo/api/push';

/**
 * Push-уведомления в браузере через Firebase Cloud Messaging — единый механизм
 * с мобильным приложением (см. FcmService.java): оба регистрируют FCM-токен через
 * тот же POST /api/push/device-token (platform WEB для веба, ANDROID/IOS для
 * мобилки), и оба получают push через один и тот же backend-вызов
 * FcmService.sendToUser(). Раньше веб использовал отдельный самописный Web Push
 * (VAPID, ручное AES128GCM-шифрование) — часто ловил проблемы с форматом
 * endpoint'а подписки; Firebase JS SDK берёт это на себя.
 */
@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private app: FirebaseApp | null = null;
  private messaging: Messaging | null = null;
  private currentToken: string | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Инициализировать и подписаться на push (вызывать после успешного логина).
   */
  async subscribeAndRegister(): Promise<void> {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      console.warn('[Push] Skipped: no Service Worker support');
      return;
    }

    try {
      console.log('[Push] Registering Firebase messaging Service Worker...');
      const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
      await navigator.serviceWorker.ready;

      console.log('[Push] Fetching VAPID public key...');
      const keyResponse = await firstValueFrom(this.http.get<{ publicKey: string }>(`${API_URL}/vapid-public-key`));
      const vapidPublicKey = keyResponse?.publicKey || null;

      if (!vapidPublicKey) {
        console.warn('[Push] No VAPID key from server — check auth and backend config');
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('[Push] Notification permission not granted:', permission);
        return;
      }

      this.app = initializeApp(firebaseConfig);
      this.messaging = getMessaging(this.app);

      const token = await getToken(this.messaging, {
        vapidKey: vapidPublicKey,
        serviceWorkerRegistration: swRegistration
      });

      if (!token) {
        console.warn('[Push] Could not obtain FCM token');
        return;
      }

      this.currentToken = token;
      console.log('[Push] Sending FCM token to backend...');
      await firstValueFrom(this.http.post(`${API_URL}/device-token`, { token, platform: 'WEB' }));
      console.log('[Push] Token registered successfully');

      // Foreground-сообщения Firebase не показывает сам — показываем вручную.
      onMessage(this.messaging, (payload) => {
        console.log('[Push] Foreground message received:', payload);
        const title = payload.notification?.title || payload.data?.['title'] || 'Bilload';
        const body = payload.notification?.body || payload.data?.['body'] || '';
        const url = payload.data?.['url'] || '/';
        if (Notification.permission === 'granted') {
          const notification = new Notification(title, { body, icon: '/assets/app-icon-192.png' });
          notification.onclick = () => {
            window.focus();
            window.location.href = url;
          };
        }
      });
    } catch (e: unknown) {
      const err = e as { status?: number; message?: string; error?: unknown };
      console.warn('[Push] Subscribe failed:', err?.status ?? err?.message ?? e);
    }
  }

  /** Отписаться от push и удалить токен на бэкенде (вызывать при логауте). */
  async unregister(): Promise<void> {
    try {
      if (this.messaging) {
        await deleteToken(this.messaging);
      }
      if (this.currentToken) {
        await firstValueFrom(
          this.http.delete(`${API_URL}/device-token`, { params: { token: this.currentToken } })
        );
        this.currentToken = null;
      }
    } catch (e) {
      console.warn('[Push] Unregister failed:', e);
    }
  }

  /**
   * Показать уведомление прямо со страницы (не push). Если оно появится — разрешение есть, проблема в SW.
   * В консоли: ng.getComponent(document.querySelector('app-root')).pushService.showLocalTestNotification()
   */
  async showLocalTestNotification(): Promise<void> {
    if (typeof Notification === 'undefined') {
      console.warn('[Push] Notification API not available');
      return;
    }
    const p = await Notification.requestPermission();
    console.log('[Push] Permission:', p);
    if (p !== 'granted') {
      console.warn('[Push] Permission not granted, cannot show notification');
      return;
    }
    new Notification('Bilload (локальный тест)', { body: 'Если видите это — уведомления работают со страницы.' });
    console.log('[Push] Local notification created');
  }

  /** Отправить тестовое push с бэкенда (POST /api/push/test). */
  sendTestNotification(): Promise<void> {
    return firstValueFrom(this.http.post<void>(`${API_URL}/test`, {})).then(
      () => console.log('[Push] Test notification sent — check browser/OS'),
      (e) => console.warn('[Push] Test send failed:', e)
    );
  }
}
